import * as React from 'react';
import { connect } from 'react-redux';
import { convertKgToLbs, convertLbsToKg } from '../../../utils/weight-conversion';
import omit from '../../../utils/omit';

import {
  ADD_PASSENGER,
  DEFAULT_PASSENGER,
  EDIT_PASSENGER,
  WEIGHT_UNITS
} from '../weight-and-balance.consts';
import { handleCloseModal } from '../../calendar.model';
import { getWeightAndBalance, saveWeightAndBalance } from '../weight-and-balance.actions';
import { DECIMAL_NON_ZERO_PATTERN } from '../../../utils/validation';
import InvalidData from '../../../ui/error-states/invalid-data';
import {
  convertToAbsoluteWeight,
  getDefaultSeats,
  getPredefinedPassengers
} from '../weight-and-balance.helpers';
import EventContainer from '../../modals/calendar.event-container';
import PassengerMove from './passenger-move';
import type { AvailablePassengerType } from '../weight-and-balance.types';

class PassengerModal extends React.Component {
  static footer = true;

  constructor(props: Props) {
    super(props);

    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleSelectAvailable = this.handleSelectAvailable.bind(this);
    this.handleClose = handleCloseModal.bind(this, this.props);

    this.state = this.getNormalizedPassenger();
  }

  async componentDidMount() {
    const {
      URL: {
        eventParams: { event }
      },
      weightAndBalance: { rows, eventId },
      getWeightAndBalance
    } = this.props;

    if (!rows || event !== eventId) {
      await getWeightAndBalance();
      this.setState(this.getNormalizedPassenger());
    }
  }

  getNormalizedPassenger() {
    const {
      URL: { eventParams },
      weightAndBalance: { passengers, rows }
    } = this.props;

    const col = Number(eventParams.col);
    const row = Number(eventParams.row);

    const passenger = passengers && passengers[row] && passengers[row][col];

    return {
      ...DEFAULT_PASSENGER,
      ...passenger,
      isValid: Boolean(rows && rows[row] && rows[row].seats > col),
      moveSectionOpen: false,
    };
  }

  handleChange(name) {
    return e => this.setState({ [name]: e.target.value });
  }

  async handleSubmit(e) {
    e.preventDefault();

    const { unit, weight } = this.state;
    const {
      URL: {
        eventParams: { row, col }
      },
      saveWeightAndBalance,
      weightAndBalance: { passengers, rows }
    } = this.props;

    const updatedPassengers = (passengers || rows).map((_row, r) =>
      (Array.isArray(_row) ? _row : getDefaultSeats(_row.seats)).map((_col, c) =>
        Number(row) === r && Number(col) === c
          ? {
              ...omit(this.state, ['isValid']),
              weight: convertToAbsoluteWeight(weight, unit)
            }
          : _col
      )
    );

    await saveWeightAndBalance({
      passengers: updatedPassengers
    });

    this.handleClose();
  }

  handleSelectAvailable(passenger: AvailablePassengerType) {
    return () => {
      this.setState(omit(passenger, 'type'));
    };
  }

  toggleMoveSection = () => {
    const {moveSectionOpen} = this.state;

      this.setState({
        moveSectionOpen: !moveSectionOpen,
      });
  }

  render() {
    const {
      URL: {
        eventParams: { action, col, row }
      },
      UI,
      weightAndBalance: { availablePassengers, passengers }
    } = this.props;
    const {
      fullName,
      weight,
      unit,
      isValid,
      phoneNumber,
      email,
      bookingId,
      bookingUrl,
      moveSectionOpen
    } = this.state;

    if (!isValid && !UI.progress) {
      return (
        <InvalidData hint="Looks like you have expired url. Try to edit passengers through Weight & Balance modal" />
      );
    }

    const modalType = action === ADD_PASSENGER ? ADD_PASSENGER : EDIT_PASSENGER;

    const predefinedPassengers = getPredefinedPassengers(
      availablePassengers || [],
      passengers || [],
      Number(row),
      Number(col),
      fullName
    );

    return (
      <EventContainer>
        <div className="passenger-modal">
          <form onSubmit={this.handleSubmit}>
            <div className="passenger-info-block">
              <div className="display-flex">
                <h6 className="text-bold text-muted">Main info</h6>

                <div className="passenger-field">
                  <label className="required-label" htmlFor="passenger-first-name">
                    Full Name
                  </label>

                  <input
                    id="passenger-first-name"
                    type="text"
                    title="First Name"
                    className="form-control"
                    value={fullName}
                    required
                    onChange={this.handleChange('fullName')}
                  />
                </div>

                <div className="passenger-field">
                  <div className="display-flex">
                    <div className="width-100 m-r-6">
                      <label className="passenger-label" htmlFor="passenger-weight">
                        Weight
                      </label>

                      <input
                        id="passenger-weight"
                        type="text"
                        title="weight"
                        size="8"
                        className="form-control"
                        value={weight}
                        pattern={DECIMAL_NON_ZERO_PATTERN}
                        onChange={this.handleChange('weight')}
                      />
                    </div>

                    <div className="width-50">
                      <label className="passenger-label" htmlFor="weight-unit">
                        Units
                      </label>

                      <select
                        id="weight-unit"
                        onChange={this.handleChange('unit')}
                        className="form-control"
                        value={unit}
                      >
                        <option value={WEIGHT_UNITS.lbs}>{WEIGHT_UNITS.lbs}</option>
                        <option value={WEIGHT_UNITS.kg}>{WEIGHT_UNITS.kg}</option>
                      </select>
                    </div>
                  </div>

                  {Number(weight) ? (
                    <p className="description-text-gray">
                      {unit === WEIGHT_UNITS.lbs
                        ? `${convertLbsToKg(weight)} ${WEIGHT_UNITS.kg}`
                        : `${convertKgToLbs(weight)} ${WEIGHT_UNITS.lbs}`}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="display-flex">
                <h6 className="text-bold text-muted">Additional info</h6>

                <div className="passenger-field">
                  <label className="passenger-label" htmlFor="passenger-phone">
                    Phone number
                  </label>

                  <input
                    id="passenger-phone"
                    type="text"
                    title="Phone number"
                    className="form-control"
                    value={phoneNumber}
                    onChange={this.handleChange('phoneNumber')}
                  />
                </div>

                <div className="passenger-field">
                  <label className="passenger-label" htmlFor="passenger-email">
                    Email
                  </label>

                  <input
                    id="passenger-email"
                    type="email"
                    title="Email"
                    className="form-control"
                    value={email}
                    onChange={this.handleChange('email')}
                  />
                </div>
              </div>
            </div>

            {predefinedPassengers.length ? (
              <div>
                <div className="text-bold text-primary passenger-label">
                  Choose from event participants:
                </div>

                <div className="available-passengers-block">
                  {predefinedPassengers.map((passenger, index) => (
                    <span key={index}>
                      <span className="text-bold">{passenger.type}</span> - {passenger.fullName}
                      {fullName === passenger.fullName ? (
                        <button
                          className="btn btn-link btn-xs margin-left-5 text-bold"
                          type="button"
                          disabled
                        >
                          Applied
                        </button>
                      ) : (
                        <button
                          onClick={this.handleSelectAvailable(passenger)}
                          className="btn btn-link btn-xs margin-left-5 text-bold"
                          type="button"
                        >
                          Apply
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}

            {bookingId && bookingUrl ? (
              <React.Fragment>
                <label>Booking &nbsp;</label>
                <a target="_blank" rel="noopener noreferrer" href={bookingUrl}>
                  {bookingId}
                </a>
              </React.Fragment>
            ) : null}

            <input
              id={`${modalType}-cancel`}
              onClick={this.handleClose}
              type="button"
              style={{ display: 'none' }}
            />

            <input id={modalType} type="submit" style={{ display: 'none' }} />
          </form>
          <div className="display-flex top-indent">
            <b
              className="margin-top-5 cursor-pointer text-bold text-primary passenger-label"
              onClick={this.toggleMoveSection}
            >
              Move passenger <small className={`icon ${moveSectionOpen ? "up-open-mini": "down-open-mini"}`}></small>
            </b>
          </div>
          {moveSectionOpen && isValid && modalType === EDIT_PASSENGER ? (
            <PassengerMove
              row={Number(row)}
              seat={Number(col)}
              passengerName={fullName}
              onMove={this.handleClose}
            />
          ) : null}
        </div>
      </EventContainer>
    );
  }
}

const mapStateToProps = state => ({
  UI: state.UI,
  URL: state.URL,
  scrollY: state.userUI.scrollY,
  weightAndBalance: state.weightAndBalance
});

export default connect(
  mapStateToProps,
  {
    getWeightAndBalance,
    saveWeightAndBalance
  }
)(PassengerModal);
